import { HealthInsuranceData, PopulationData, PvCostData, PvSizeData, supabaseClient, VMTData } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

export class DataService {
  // Population data
  static async getPopulationData(): Promise<PopulationData[]> {
    const { data, error } = await supabaseClient
      .from('population')
      .select('*')
      .order('year', { ascending: true });
    
    if (error) throw error;
    return data || [];
  }

  // Health insurance data
  static async getHealthInsuranceData(location?: string): Promise<HealthInsuranceData[]> {
    let query = supabaseClient
      .from('percent_no_health_insure')
      .select('*')
      .order('year', { ascending: true });
    
    if (location) {
      query = query.ilike('location_name', `%${location}%`);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  // PV Cost data
  static async getPvCostData(location?: string): Promise<PvCostData[]> {
    let query = supabaseClient
      .from('all_pv_cost')
      .select('*')
      .order('year', { ascending: true });
    
    if (location) {
      query = query.ilike('location_name', `%${location}%`);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  // PV Size data
  static async getPvSizeData(location?: string): Promise<PvSizeData[]> {
    let query = supabaseClient
      .from('all_pv_size')
      .select('*')
      .order('year', { ascending: true });
    
    if (location) {
      query = query.ilike('location_name', `%${location}%`);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  // Get all available locations
  static async getAvailableLocations(): Promise<string[]> {
    const { data, error } = await supabaseClient
      .from('population')
      .select('location_name')
      .order('location_name');
    
    if (error) throw error;
    // Fix TypeScript Set iteration issue
    return Array.from(new Set(data?.map(row => row.location_name) || []));
  }

  // Get city-county mappings from Supabase location service
  static async getCityCountyMappings(): Promise<Record<string, string>> {
    try {
      // Ensure the location service is initialized
      await supabaseLocationService.initialize();
      
      // Get all available locations
      const locations = supabaseLocationService.getAvailableLocations();
      
      // Build city-county mappings
      const mappings: Record<string, string> = {};
      
      locations.forEach(location => {
        if (location.type === 'city' && location.county) {
          mappings[location.name.toLowerCase()] = location.county;
        }
      });
      
      return mappings;
      
    } catch (error) {
      console.error('Error loading city-county mappings from Supabase:', error);
      // Return empty mappings as fallback
      return {};
    }
  }

  // VMT data by mode (raw Replica data)
  static async getVMTData(location?: string, modes?: string[], geofips?: number): Promise<VMTData[]> {
    // Build a strictly filtered query (no broad sampling)
    let query = supabaseClient
      .from('replica_vmt_by_mode')
      .select('*')
      .order('year', { ascending: true });
    
    if (geofips) {
      query = query.eq('geofips', geofips);
    } else if (location) {
      query = query.ilike('location_name', `%${location}%`);
    }
    
    if (modes && modes.length > 0) {
      query = query.in('mode', modes);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('VMT query error:', error);
      throw error;
    }
    
    return data || [];
  }

  // VMT data by geofips (raw Replica data)
  static async getVMTDataByGeofips(geofips?: number): Promise<VMTData[]> {
    
    if (!geofips) {
      console.error('Geofips is required for VMT data lookup');
      throw new Error('Geofips is required for VMT data lookup');
    }
    
    try {
      const { data, error } = await supabaseClient
        .from('replica_vmt_by_mode')
        .select('*')
        .eq('geofips', geofips)
        .order('year', { ascending: true });

      if (error) {
        console.error('Error fetching VMT data by geofips:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getVMTDataByGeofips:', error);
      throw error;
    }
  }

  // VMT data with AADT scaling factors (more accurate historical data)
  static async getVMTDataWithScaling(geofips?: number): Promise<VMTData[]> {
    
    if (!geofips) {
      console.error('Geofips is required for VMT scaling calculation');
      throw new Error('Geofips is required for VMT scaling calculation');
    }

    try {
            // Get the 2023 VMT data for the location using geofips
      let { data: vmt2023Data, error: vmtError } = await supabaseClient
        .from('replica_vmt_by_mode')
        .select('*')
        .eq('geofips', geofips)
        .eq('year', 2023);

      if (vmtError) {
        console.error('Error fetching 2023 VMT data:', vmtError);
        throw vmtError;
      }

      // If no 2023 data, try to get the latest available year as fallback
      if (!vmt2023Data || vmt2023Data.length === 0) {
        console.warn(`No 2023 VMT data found for geofips ${geofips}, trying to get latest available year...`);
        const { data: latestVmtData, error: latestError } = await supabaseClient
          .from('replica_vmt_by_mode')
          .select('*')
          .eq('geofips', geofips)
          .order('year', { ascending: false })
          .limit(100); // Get recent years
        
        if (!latestError && latestVmtData && latestVmtData.length > 0) {
          // Get the latest year from the data
          const latestYear = Math.max(...latestVmtData.map((d: any) => d.year));
          console.warn(`Using ${latestYear} VMT data as fallback for geofips ${geofips}`);
          
          // Filter to only the latest year's data
          vmt2023Data = latestVmtData.filter((d: any) => d.year === latestYear);
          
          // Adjust years: if we're using 2022 data, we'll need to project forward to 2023
          // For now, we'll use the latest year's data and adjust in the loop below
        } else {
          console.error(`No VMT data found for geofips ${geofips} in any year`);
          return [];
        }
      }

      // Fetch AADT data for backcasting (optional - table may not exist)
      let aadtData = null;
      try {
        const { data, error: aadtError } = await supabaseClient
          .from('aadt_filtered')
          .select('year, value')
          .eq('geofips', geofips)
          .order('year', { ascending: true });
        
        if (aadtError) {
          console.warn('AADT table not available, using EMFAC data only:', aadtError);
          aadtData = null;
        } else {
          aadtData = data;
        }
      } catch (err) {
        console.warn('AADT table not available, using EMFAC data only');
        aadtData = null;
      }


      // Determine the baseline year (might be 2023 or earlier if 2023 data not available)
      const baselineYear = vmt2023Data.length > 0 ? vmt2023Data[0].year : 2023;
      console.log(`Using baseline year ${baselineYear} for VMT calculations`);

      // Create backcasted VMT data for each mode using cumulative backcasting
      const backcastedData: VMTData[] = [];
      
      for (const mode2023 of vmt2023Data) {
        const mode = mode2023.mode;
        const vmt2023 = mode2023.vmt;
        const baseYear = mode2023.year || baselineYear;
        
        
        // Sort AADT data by year descending (2023, 2022, 2021, ...)
        const sortedAadtData = (aadtData || []).sort((a, b) => b.year - a.year);
        
        // Start with baseline year data
        backcastedData.push({
          ...mode2023,
          year: baseYear
        });
        
        // If baseline is not 2023, add 2023 data using the same VMT (reasonable assumption)
        if (baseYear < 2023) {
          backcastedData.push({
            ...mode2023,
            year: 2023,
            vmt: vmt2023
          });
        }
        
        // Backcast year by year from 2023 down to 2015
        // Formula: VMT_year = VMT_next_year × AADT_ratio_year
        // Example: VMT_2022 = VMT_2023 × AADT_2022, VMT_2021 = VMT_2022 × AADT_2021, etc.
        let nextYearVMT = vmt2023; // Start with 2023 VMT from Replica
        
        // Create a map of AADT ratios by year for quick lookup
        const aadtByYear = new Map<number, number>();
        sortedAadtData.forEach(aadt => {
          if (aadt.year >= 2015 && aadt.year <= 2022) {
            aadtByYear.set(aadt.year, aadt.value);
          }
        });
        
        // Iterate backwards from 2022 to 2015, calculating each year's VMT
        for (let year = 2022; year >= 2015; year--) {
          const aadtRatio = aadtByYear.get(year);
          
          if (aadtRatio && aadtRatio > 0) {
            // Calculate: VMT_year = VMT_next_year × AADT_ratio_year
            const vmtForYear = Math.round(nextYearVMT * aadtRatio);
            
            // Only add if we don't already have data for this year
            if (!backcastedData.find(d => d.year === year && d.mode === mode)) {
              backcastedData.push({
                ...mode2023,
                year: year,
                vmt: vmtForYear
              });
            }
            
            // Update nextYearVMT for next iteration (going backwards)
            nextYearVMT = vmtForYear;
            
            if (year === 2022) {
              console.log(`✅ DataService: Backcasted ${year} VMT for mode ${mode}, geofips ${geofips}: ${vmtForYear.toLocaleString()} (from 2023 VMT: ${vmt2023.toLocaleString()}, AADT ${year}: ${aadtRatio})`);
            }
          } else {
            // If AADT ratio missing, use next year's VMT (no change assumption)
            console.warn(`⚠️ DataService: No AADT ratio for year ${year}, mode ${mode}, geofips ${geofips}. Using next year VMT: ${nextYearVMT.toLocaleString()}`);
            
            if (!backcastedData.find(d => d.year === year && d.mode === mode)) {
              backcastedData.push({
                ...mode2023,
                year: year,
                vmt: nextYearVMT
              });
            }
            // Keep nextYearVMT the same for next iteration
          }
        }
      }

      // If no AADT data available, fall back to EMFAC data for historical years
      if (!aadtData || aadtData.length === 0) {
        console.log('No AADT data available, using EMFAC data for historical reconstruction');
        
        // Get EMFAC data for the county
        const emfacData = await this.getEMFACBackcastingDataByGeofips(geofips);
        
        // Add EMFAC historical data (2005-2014) to the backcasted data
        for (const mode2023 of vmt2023Data) {
          for (const emfacRecord of emfacData) {
            backcastedData.push({
              ...mode2023,
              year: emfacRecord.year,
              vmt: Math.round(mode2023.vmt * emfacRecord.ratio)
            });
          }
        }
      }

      return backcastedData;
      
    } catch (error) {
      console.error('Error in getVMTDataWithScaling:', error);
      throw error;
    }
  }

  // EMFAC backcasting data for VMT historical reconstruction
  static async getEMFACBackcastingData(location: string, years: number[] = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014]): Promise<any[]> {
    
    try {
      // First, determine the county for the given location
      let countyName = location;
      
      // If it's a city, we need to map it to its county
      if (!location.toLowerCase().includes('county')) {
        
        // Use the existing city-county mapping logic from getCityCountyMappings
        const mappings = await this.getCityCountyMappings();
        const locationKey = location.toLowerCase();
        
        // Try to find a mapping
        const mapping = Object.entries(mappings).find(([city, county]) => 
          city.toLowerCase().includes(locationKey) || 
          locationKey.includes(city.toLowerCase())
        );
        
        if (mapping) {
          countyName = mapping[1];
        } else {
        }
      }
      
      // Fetch EMFAC data for the county
      const { data, error } = await supabaseClient
        .from('emfac_county_filtered')
        .select('*')
        .ilike('location_name', `%${countyName}%`)
        .in('year', years)
        .in('class', ['LDV', 'HDV']) // Light Duty Vehicles and Heavy Duty Vehicles
        .order('year', { ascending: true });
      
      if (error) {
        console.error('EMFAC query error:', error);
        // If the table doesn't exist or there's an error, return empty array instead of throwing
        console.warn('EMFAC table may not exist yet, returning empty data');
        return [];
      }
      
      return data || [];
      
    } catch (error) {
      console.error('Error in getEMFACBackcastingData:', error);
      console.warn('Returning empty EMFAC data due to error');
      return [];
    }
  }

  // EMFAC backcasting data by geofips
  static async getEMFACBackcastingDataByGeofips(geofips?: number, years: number[] = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014]): Promise<any[]> {
    
    if (!geofips) {
      console.error('Geofips is required for EMFAC data lookup');
      throw new Error('Geofips is required for EMFAC data lookup');
    }
    
    try {
      // First, get the location info to determine if it's a city or county
      const { data: locationData, error: locationError } = await supabaseClient
        .from('population')
        .select('location_name, geography_type, county')
        .eq('geofips', geofips)
        .limit(1);

      if (locationError) {
        console.error('Error fetching location data for geofips:', locationError);
        throw locationError;
      }

      if (!locationData || locationData.length === 0) {
        return [];
      }

      const locationInfo = locationData[0];

      // For EMFAC data, we need to use county-level data
      // If it's a city, we need to find the corresponding county
      let countyGeofips = geofips;
      
      if (locationInfo.geography_type === 'City') {
        // For cities, we need to find the county geofips
        
        // Get the county name from the location info
        const countyName = locationInfo.county || `${locationInfo.location_name} County`;
        
        // Find the county geofips
        const { data: countyData, error: countyError } = await supabaseClient
          .from('population')
          .select('geofips')
          .eq('location_name', countyName)
          .eq('geography_type', 'County')
          .limit(1);

        if (countyError) {
          console.error('Error finding county geofips:', countyError);
          return [];
        }

        if (countyData && countyData.length > 0) {
          countyGeofips = countyData[0].geofips;
        }
      }

      // Fetch EMFAC data for the county geofips (include 2015 for baseline)
      const allYears = [...years, 2015]; // Include 2015 for baseline calculation
      console.log('Fetching EMFAC data for county geofips:', countyGeofips, 'years:', allYears);
      const { data, error } = await supabaseClient
        .from('emfac_county_filtered')
        .select('*')
        .eq('geofips', countyGeofips)
        .in('year', allYears)
        .in('class', ['LDV', 'HDV']) // Light Duty Vehicles and Heavy Duty Vehicles
        .order('year', { ascending: true })
        .limit(50000); // High limit to ensure we get all data

      if (error) {
        console.error('Error fetching EMFAC data by geofips:', error);
        throw error;
      }

      console.log('Raw EMFAC data from database:', data);

      // Process the data to create ratio fields for backcasting
      if (data && data.length > 0) {
        // Get 2015 LDV data as baseline
        const emfac2015LDV = data.find(record => record.year === 2015 && record.class === 'LDV');
        const emfac2015LDVVMT = emfac2015LDV ? emfac2015LDV.total_vmt : 0;

        console.log('EMFAC 2015 LDV data:', emfac2015LDV);
        console.log('EMFAC 2015 LDV VMT:', emfac2015LDVVMT);

        // Create ratio data for each year using LDV only
        const ratioData = [];
        for (const year of years) {
          const yearLDVData = data.find(record => record.year === year && record.class === 'LDV');
          if (yearLDVData && emfac2015LDVVMT > 0) {
            const yearLDVVMT = yearLDVData.total_vmt;
            const ratio = yearLDVVMT / emfac2015LDVVMT;
            
            console.log(`EMFAC ratio calculation for ${year}: yearLDVVMT=${yearLDVVMT}, emfac2015LDVVMT=${emfac2015LDVVMT}, ratio=${ratio}`);
            
            ratioData.push({
              year: year,
              ratio: ratio,
              total_vmt: yearLDVVMT
            });
          } else {
            console.log(`No EMFAC LDV data found for year ${year}`);
          }
        }

        return ratioData;
      }

      return [];
      
    } catch (error) {
      console.error('Error in getEMFACBackcastingDataByGeofips:', error);
      console.warn('Returning empty EMFAC data due to error');
      return [];
    }
  }

  // VMT per capita data using the exact same VMT values as the main VMT chart
  static async getVMTPerCapitaData(geofips?: number): Promise<any[]> {
    if (!geofips) {
      console.error('Geofips is required for VMT per capita calculation');
      throw new Error('Geofips is required for VMT per capita calculation');
    }

    try {
      console.log('Calculating VMT per capita using exact same VMT as main chart for geofips:', geofips);

      // Get VMT data using the EXACT same method as the main VMT chart
      const locationVMTData = await this.getVMTDataWithScaling(geofips);
      
      if (!locationVMTData || locationVMTData.length === 0) {
        console.error('No VMT data found for location with geofips:', geofips);
        throw new Error('No VMT data available for this location');
      }

      // Get population data for the selected location
      const locationName = locationVMTData[0]?.location_name;
      console.log('Location name from VMT data:', locationName);

      // Try to get population by geofips first, then fallback to location_name lookup
      let locationPopulationData: any[] = [];
      
      const { data: popByGeofips, error: popGeofipsError } = await supabaseClient
        .from('population')
        .select('year, population, geofips')
        .eq('geofips', geofips)
        .order('year', { ascending: true });

      if (popGeofipsError || !popByGeofips || popByGeofips.length === 0) {
        console.warn('No population data found by geofips, trying location name lookup...');
        
        const normalizedLocationName = locationName.toLowerCase().trim();
        const { data: popByName, error: popNameError } = await supabaseClient
          .from('population')
          .select('year, population, location_name, geofips')
          .or(`location_name.ilike.%${normalizedLocationName}%`)
          .order('year', { ascending: true });

        if (popNameError || !popByName || popByName.length === 0) {
          console.error('No population data found by location name either');
          throw new Error(`No population data found for ${locationName} (geofips: ${geofips})`);
        } else {
          locationPopulationData = popByName;
          console.log('Found population data by location name:', popByName.length, 'records');
        }
      } else {
        locationPopulationData = popByGeofips;
        console.log('Found population data by geofips:', popByGeofips.length, 'records');
      }

      // Use the exact same VMT calculation as the main VMT chart:
      // Sum PRIVATE_AUTO and ON_DEMAND_AUTO modes for each year
      const vmtByYear = locationVMTData
        .filter(d => d.mode === 'PRIVATE_AUTO' || d.mode === 'ON_DEMAND_AUTO')
        .reduce((acc: any, curr) => {
          const existing = acc.find((item: any) => item.year === curr.year);
          if (existing) {
            existing.total_vmt += curr.vmt;
          } else {
            acc.push({
              year: curr.year,
              total_vmt: curr.vmt,
              location_name: curr.location_name
            });
          }
          return acc;
        }, [])
        .sort((a: any, b: any) => a.year - b.year);

      console.log('VMT by year (matching main chart logic):', vmtByYear);

      // Calculate VMT per capita for selected location (VMT from main chart ÷ population)
      const locationVMTPerCapita = vmtByYear.map((vmt: any) => {
        const population = locationPopulationData.find(p => p.year === vmt.year);
        const vmtPerCapita = population && population.population > 0 ? vmt.total_vmt / population.population : 0;
        
        console.log(`${locationName} Year ${vmt.year}: VMT=${vmt.total_vmt.toLocaleString()}, Population=${population?.population?.toLocaleString() || 'NOT FOUND'}, VMT per capita=${vmtPerCapita.toFixed(1)}`);
        
        return {
          year: vmt.year,
          vmt_per_capita: vmtPerCapita,
          location: 'Selected Location'
        };
      });

      // Calculate California average using the same approach
      // Get California state-level VMT using same methodology
      console.log('Calculating California average using same VMT methodology...');
      
      // Use LA County as a large representative sample and scale appropriately
      const californiaProxyVMT = await this.getVMTDataWithScaling(6037); // Los Angeles County
      
      const californiaVMTByYear = californiaProxyVMT
        .filter(d => d.mode === 'PRIVATE_AUTO' || d.mode === 'ON_DEMAND_AUTO')
        .reduce((acc: any, curr) => {
          const existing = acc.find((item: any) => item.year === curr.year);
          if (existing) {
            existing.total_vmt += curr.vmt;
          } else {
            acc.push({
              year: curr.year,
              total_vmt: curr.vmt
            });
          }
          return acc;
        }, [])
        .sort((a: any, b: any) => a.year - b.year);

      // Get California total population
      const { data: calPopData, error: calPopError } = await supabaseClient
        .from('population')
        .select('year, population')
        .order('year', { ascending: true });

      if (calPopError) {
        console.error('Error fetching California population data:', calPopError);
        throw calPopError;
      }

      // Aggregate California population by year
      const californiaPopByYear = calPopData
        .reduce((acc: any, curr) => {
          const existing = acc.find((item: any) => item.year === curr.year);
          if (existing) {
            existing.total_population += curr.population || 0;
          } else {
            acc.push({
              year: curr.year,
              total_population: curr.population || 0
            });
          }
          return acc;
        }, []);

      // Calculate California VMT per capita
      // Scale LA County VMT to represent California (LA County is ~25% of CA population)
      const californiaVMTPerCapita = californiaVMTByYear.map((vmt: any) => {
        const population = californiaPopByYear.find((p: any) => p.year === vmt.year);
        
        // Scale LA County VMT to approximate California state total
        // LA County represents about 25% of California's population and has higher VMT than state average
        const scalingFactor = 4.0; // Rough approximation to scale from LA County to state level
        const stateVMT = vmt.total_vmt * scalingFactor;
        
        const vmtPerCapita = population && population.total_population > 0 ? stateVMT / population.total_population : 0;
        
        console.log(`California Year ${vmt.year}: Scaled VMT=${stateVMT.toLocaleString()}, Population=${population?.total_population?.toLocaleString() || 'NOT FOUND'}, VMT per capita=${vmtPerCapita.toFixed(1)}`);
        
        return {
          year: vmt.year,
          vmt_per_capita: vmtPerCapita,
          location: 'California Average'
        };
      });

      // Combine the data
      const combinedData = [...locationVMTPerCapita, ...californiaVMTPerCapita];

      console.log('Final VMT per capita data - using exact same VMT as main chart:');
      console.log('Location data points:', locationVMTPerCapita.length);
      console.log('California data points:', californiaVMTPerCapita.length);

      return combinedData;

    } catch (error) {
      console.error('Error in getVMTPerCapitaData:', error);
      throw error;
    }
  }
}
